const ALLOWED_TAGS = new Set(['P', 'DIV', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'SPAN', 'H1', 'H2', 'H3', 'UL', 'OL', 'LI', 'BLOCKQUOTE']);
const ALLOWED_STYLES = new Set(['font-family', 'font-size', 'color', 'background-color', 'text-align', 'margin-left']);
export const CLIENT_SUMMARY_LIST_CLASSES = new Set([
  'cs-list-disc', 'cs-list-circle', 'cs-list-square', 'cs-list-dash', 'cs-list-check', 'cs-list-arrow',
  'cs-list-decimal', 'cs-list-upper-alpha', 'cs-list-lower-alpha', 'cs-list-upper-roman', 'cs-list-lower-roman',
]);

const safeStyle = (styleText = '') => styleText.split(';').map((declaration) => {
  const separator = declaration.indexOf(':');
  if (separator < 0) return '';
  const property = declaration.slice(0, separator).trim().toLowerCase();
  const value = declaration.slice(separator + 1).trim();
  if (!ALLOWED_STYLES.has(property) || !value || /url\s*\(|expression\s*\(|javascript:/i.test(value)) return '';
  if ((property === 'color' || property === 'background-color') && !/^(#[0-9a-f]{3,8}|rgba?\([\d\s,.%]+\)|[a-z]+)$/i.test(value)) return '';
  if (property === 'font-size' && !/^(?:[8-9]|[1-6]\d|72)(?:px|pt)$/i.test(value)) return '';
  if (property === 'text-align' && !/^(left|center|right|justify)$/i.test(value)) return '';
  if (property === 'margin-left' && !/^\d{1,3}(?:px|em)$/i.test(value)) return '';
  if (property === 'font-family' && !/^[\w\s,"'-]+$/.test(value)) return '';
  return `${property}: ${value}`;
}).filter(Boolean).join('; ');

export const sanitizeClientSummaryHtml = (html = '') => {
  if (typeof DOMParser === 'undefined') return String(html || '');
  const documentNode = new DOMParser().parseFromString(`<div>${String(html || '')}</div>`, 'text/html');
  const root = documentNode.body.firstElementChild;
  const cleanNode = (node, outputDocument) => {
    if (node.nodeType === 3) return outputDocument.createTextNode(node.textContent || '');
    if (node.nodeType !== 1) return outputDocument.createTextNode('');
    if (['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'MATH'].includes(node.tagName)) return outputDocument.createTextNode('');
    const fragment = ALLOWED_TAGS.has(node.tagName)
      ? outputDocument.createElement(node.tagName.toLowerCase())
      : outputDocument.createDocumentFragment();
    if (fragment.nodeType === 1) {
      const style = safeStyle(node.getAttribute('style') || '');
      if (style) fragment.setAttribute('style', style);
      const listClass = node.getAttribute('class') || '';
      if ((node.tagName === 'UL' || node.tagName === 'OL') && CLIENT_SUMMARY_LIST_CLASSES.has(listClass)) fragment.setAttribute('class', listClass);
    }
    [...node.childNodes].forEach((child) => fragment.appendChild(cleanNode(child, outputDocument)));
    return fragment;
  };
  const output = documentNode.implementation.createHTMLDocument('');
  const container = output.createElement('div');
  [...root.childNodes].forEach((node) => container.appendChild(cleanNode(node, output)));
  return container.innerHTML;
};

export const plainTextToClientSummaryHtml = (content = '') => {
  if (/<\/?(?:p|div|br|strong|b|em|i|u|span|h[1-3]|ul|ol|li|blockquote)\b/i.test(String(content))) {
    return sanitizeClientSummaryHtml(content);
  }
  if (typeof document === 'undefined') return String(content || '');
  return String(content || '').split(/\n{2,}/).map((paragraph) => {
    const element = document.createElement('p');
    paragraph.split('\n').forEach((line, index) => {
      if (index) element.appendChild(document.createElement('br'));
      element.appendChild(document.createTextNode(line));
    });
    return element.outerHTML;
  }).join('');
};

export const clientSummaryHtmlToText = (content = '') => {
  if (typeof DOMParser === 'undefined') return String(content || '').replace(/<[^>]*>/g, ' ');
  const documentNode = new DOMParser().parseFromString(sanitizeClientSummaryHtml(content), 'text/html');
  documentNode.querySelectorAll('br').forEach((node) => node.replaceWith('\n'));
  documentNode.querySelectorAll('li > ul, li > ol').forEach((node) => { node.before('\n'); node.after('\n'); });
  documentNode.querySelectorAll('p,div,h1,h2,h3,li,blockquote').forEach((node) => node.append('\n'));
  return (documentNode.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
};

const alphaMarker = (index, upper = false) => {
  let value = ''; let number = index;
  while (number > 0) { number -= 1; value = String.fromCharCode(97 + (number % 26)) + value; number = Math.floor(number / 26); }
  return upper ? value.toUpperCase() : value;
};
const romanMarker = (index, upper = false) => {
  const numerals = [[1000,'m'],[900,'cm'],[500,'d'],[400,'cd'],[100,'c'],[90,'xc'],[50,'l'],[40,'xl'],[10,'x'],[9,'ix'],[5,'v'],[4,'iv'],[1,'i']];
  let number = index; let value = '';
  numerals.forEach(([amount, symbol]) => { while (number >= amount) { value += symbol; number -= amount; } });
  return upper ? value.toUpperCase() : value;
};

// PDF receives plain text deliberately: custom markers fall back to an ASCII bullet,
// while numbering and nesting remain visible without depending on renderer CSS support.
export const clientSummaryHtmlToPdfText = (content = '') => {
  if (typeof DOMParser === 'undefined') return clientSummaryHtmlToText(content);
  const documentNode = new DOMParser().parseFromString(sanitizeClientSummaryHtml(content), 'text/html');
  let text = '';
  const newline = () => { if (text && !text.endsWith('\n')) text += '\n'; };
  const walk = (node, depth = 0) => {
    // Generated reports can consist entirely of text nodes and inline strong tags.
    if (node.nodeType === 3) { text += node.textContent || ''; return; }
    if (node.nodeType !== 1) return;
    if (node.tagName === 'BR') { text += '\n'; return; }
    if (node.matches('ul,ol')) {
      newline();
      [...node.children].filter((child) => child.tagName === 'LI').forEach((item, itemIndex) => {
        const className = node.getAttribute('class') || '';
        let marker = 'o';
        if (node.tagName === 'OL') {
          const number = itemIndex + 1;
          if (className === 'cs-list-upper-alpha') marker = alphaMarker(number, true);
          else if (className === 'cs-list-lower-alpha') marker = alphaMarker(number);
          else if (className === 'cs-list-upper-roman') marker = romanMarker(number, true);
          else if (className === 'cs-list-lower-roman') marker = romanMarker(number);
          else marker = String(number);
        }
        text += `${depth ? `${'|  '.repeat(depth - 1)}|-- ` : ''}${marker}. `;
        [...item.childNodes].forEach((child) => walk(child, depth + 1));
        newline();
      });
      return;
    }
    const block = node.matches('p,div,h1,h2,h3,blockquote');
    if (block) newline();
    [...node.childNodes].forEach((child) => walk(child, depth));
    if (block) newline();
  };
  [...documentNode.body.childNodes].forEach((node) => walk(node));
  return text.replace(/\r\n?/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
};
